'use client';

import {
  type AbstractIntlMessages,
  NextIntlClientProvider as IntlClientProvider,
} from 'next-intl';
import { type PropsWithChildren, useCallback } from 'react';

import { logger as browserLogger } from '@/logger/logger.browser';

interface NextIntlClientProviderProps extends PropsWithChildren {
  readonly locale: string;
  readonly messages: AbstractIntlMessages;
}
const NextIntlClientProvider = ({
  children,
  locale,
  messages,
}: NextIntlClientProviderProps) => {
  const handleError = useCallback(
    (err: any) => {
      // Ignore errors if the messages data is not ready yet.
      if (!messages || !Object.keys(messages).length) {
        return;
      }

      if (
        ['MISSING_TRANSLATION', 'MISSING_DATA', 'MISSING_MESSAGE'].includes(
          err.code,
        )
      ) {
        const regex = /"([^"\\]*(\\.[^"\\]*)*)"/g;
        const [message_key, localeKey] = err.message?.match(regex) ?? [];
        browserLogger.warn('ui_translation_error', {
          cause: JSON.stringify(err),
          message_key,
          locale: localeKey,
        });
      } else {
        throw err;
      }
    },
    [messages],
  );

  return (
    // next-intl v4 removed `defaultTranslationValues`. The global rich-text
    // tags are now spread explicitly at each `t.rich()` call site.
    <IntlClientProvider
      locale={locale}
      messages={messages}
      timeZone="UTC" // default
      onError={handleError}
    >
      {children}
    </IntlClientProvider>
  );
};

export default NextIntlClientProvider;
